import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, CheckCircle, AlertCircle, X, Send, Lock,
  ChevronUp, ChevronDown, ChevronRight, Search, User, RotateCcw, Archive,
  Upload, MapPin, FileText, Trash2, Coffee, Calendar, Paperclip, Database
} from "lucide-react";
import ReopenModal from "./ReopenModal";
import InspectorTrainingButtons from "./InspectorTrainingButtons";
import CloseWithActionsModal from "./CloseWithActionsModal";
import DailyCloseReviewModal from "./DailyCloseReviewModal";
import RouteMapPanel from "./RouteMapPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const STATUS_COLORS = {
  "Ready to Route": "bg-[#2F5D8A]/12 text-[#2F5D8A] border-[#2F5D8A]/25",
  "Assigned": "bg-[#D1A054]/15 text-[#B08A3E] border-[#D1A054]/30",
  "In Field": "bg-[#A7C4A0]/20 text-[#6B8F62] border-[#A7C4A0]/40",
  "Inspection Completed": "bg-[#6B5B8D]/12 text-[#6B5B8D] border-[#6B5B8D]/25",
  "On Hold": "bg-[#EDE5D6] text-[#7A6535] border-[#D1A054]/30",
  "QA": "bg-[#2F5D8A]/12 text-[#2F5D8A] border-[#2F5D8A]/25",
};

const InspectorOrdersPage = ({ inspectorName, onBack, embedded = false, userEmail = "", autoOpenValidateRoute = false }) => {
  const navigate = useNavigate();
  const isRestrictedUser = userEmail?.toLowerCase() === 'appointment.safeguard@aarifi.com';
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [message, setMessage] = useState(null);
  const [sortBy, setSortBy] = useState("due_date");
  const [sortDir, setSortDir] = useState("asc");
  const [search, setSearch] = useState("");
  const [processing, setProcessing] = useState(null);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [viewTab, setViewTab] = useState('active'); // 'active' | 'hold' | 'qa_history'
  const [showReopenModal, setShowReopenModal] = useState(false);

  const [returnedCount, setReturnedCount] = useState(0);
  const [archivedOrders, setArchivedOrders] = useState([]);
  const [holdOrders, setHoldOrders] = useState([]);
  const [totalInQa, setTotalInQa] = useState(0);
  const [showArchived, setShowArchived] = useState(false);

  // Route Validation
  const [routeFile, setRouteFile] = useState(null);
  const [routeLoading, setRouteLoading] = useState(false);
  const [routeResult, setRouteResult] = useState(null);
  const [routeExpanded, setRouteExpanded] = useState(null);
  const [showRouteSection, setShowRouteSection] = useState(false);
  const routeFileRef = React.useRef(null);
  const [routeHistory, setRouteHistory] = useState([]);

  const fetchRouteHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/route/files/${encodeURIComponent(inspectorName)}`);
      setRouteHistory(res.data.files || []);
    } catch {}
  }, [inspectorName]);

  useEffect(() => { fetchRouteHistory(); }, [fetchRouteHistory]);

  const handleRouteValidation = async () => {
    if (!routeFile) return;
    setRouteLoading(true); setRouteResult(null);
    try {
      const fd = new FormData();
      fd.append("file", routeFile);
      fd.append("inspector_name", inspectorName);
      const res = await axios.post(`${API}/route/validate`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRouteResult(res.data);
      fetchRouteHistory();
    } catch (err) {
      setRouteResult({ error: err.response?.data?.detail || "Validation failed" });
    } finally { setRouteLoading(false); }
  };

  // Validate using the latest PDF already on file (no upload needed).
  // Report-only. Read-only endpoint — never mutates order data.
  const [pdfValidation, setPdfValidation] = useState(null);
  const [pdfValidationLoading, setPdfValidationLoading] = useState(false);
  const [pdfValidationSection, setPdfValidationSection] = useState("matched");
  const handleValidateFromLatestPdf = async () => {
    setPdfValidationLoading(true);
    setPdfValidation(null);
    try {
      const res = await axios.post(`${API}/route/validate-from-file`, { inspector_name: inspectorName });
      setPdfValidation(res.data);
      setShowRouteSection(true);
    } catch (err) {
      setPdfValidation({ error: err.response?.data?.detail || "Validation failed" });
      setShowRouteSection(true);
    } finally {
      setPdfValidationLoading(false);
    }
  };

  // ═══ Quick Assign / Unassign from Validate Route (Apr 21 2026) ═══
  // Replaces the Missing→Open Order navigation with an inline POST that
  // saves immediately and refreshes the validation report without leaving
  // the page. Also powers the Extra "Unassign Order" shortcut.
  // Uses existing endpoint `/api/orders/{id}/quick-assign-inspector`.
  const [rowBusyId, setRowBusyId] = useState("");
  const assignMissingToInspector = async (orderId, orderNumber = "") => {
    if (!orderId) return;
    setRowBusyId(orderId);
    try {
      const res = await axios.post(`${API}/orders/${orderId}/quick-assign-inspector`, {
        inspector_name: inspectorName,
        // `force: true` guarantees a re-save even if the field already matches,
        // to resolve the "currently MA-RODRIGOHAMER but not in totals" sync bug.
        force: true,
      });
      const updated = res.data || {};
      const confirmLabel = orderNumber ? `#${orderNumber}` : "Order";
      toast.success(`${confirmLabel} assigned to ${inspectorName}`, {
        description: `Status: ${updated.status || "Assigned"}`,
      });
      setMessage({ type: "success", text: `${confirmLabel} → ${inspectorName}` });
      // Refresh both the orders table + the validation report.
      await Promise.all([fetchOrders(), handleValidateFromLatestPdf()]);
    } catch (err) {
      const detail = err.response?.data?.detail || "Assign failed";
      toast.error(detail);
      setMessage({ type: "error", text: detail });
    } finally {
      setRowBusyId("");
      setTimeout(() => setMessage(null), 3500);
    }
  };
  const unassignExtraOrder = async (orderId, orderNumber = "") => {
    if (!orderId) return;
    setRowBusyId(orderId);
    try {
      const res = await axios.post(`${API}/orders/${orderId}/quick-assign-inspector`, {
        inspector_name: "",
        force: true,
      });
      const updated = res.data || {};
      const confirmLabel = orderNumber ? `#${orderNumber}` : "Order";
      toast.success(`${confirmLabel} unassigned from ${inspectorName}`, {
        description: `Status: ${updated.status || "Pending Assignment"}`,
      });
      setMessage({ type: "success", text: `${confirmLabel} unassigned` });
      await Promise.all([fetchOrders(), handleValidateFromLatestPdf()]);
    } catch (err) {
      const detail = err.response?.data?.detail || "Unassign failed";
      toast.error(detail);
      setMessage({ type: "error", text: detail });
    } finally {
      setRowBusyId("");
      setTimeout(() => setMessage(null), 3500);
    }
  };

  // Silent status fetch — populates `pdfValidation` on mount so the header
  // badge (All Set / Pending) shows immediately without opening the panel.
  // Read-only endpoint. No side effects on orders / assignments.
  // REMOVED (Apr 21 2026): the header badge moved to the Dispatch inspector
  // cards per user spec. Keeping the silent fetch would fire an unnecessary
  // PDF parse every time the inspector page opens, so it's disabled.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // (intentionally no-op)

  // Derived: retained for potential future use but no longer rendered in
  // the inspector header. Computed value stays harmless (falsy).
  const routeAllSet = !!(
    pdfValidation &&
    !pdfValidation.error &&
    (pdfValidation.pdf_addresses_total || 0) > 0 &&
    (pdfValidation.missing_in_account_total || 0) === 0 &&
    (pdfValidation.extra_in_account_total || 0) === 0
  );

  // Auto-open Validate Route when deep-linked (Apr 21 2026).
  // Triggered after the office assigns a Missing order and returns to
  // /dispatch?inspector=...&validate=1 — keeps the user in the correction flow.
  useEffect(() => {
    if (autoOpenValidateRoute) {
      handleValidateFromLatestPdf();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenValidateRoute]);

  const deleteRouteFile = async (storedFilename) => {
    if (!window.confirm("Delete this route file? Orders, assignments, and history are NOT affected.")) return;
    try {
      await axios.delete(`${API}/route/files/${encodeURIComponent(storedFilename)}`);
      await fetchRouteHistory();
      // If the deleted file was the source of the current validation view,
      // clear the stale report so it doesn't keep pointing at a missing PDF.
      setPdfValidation(null);
      setShowRouteSection(false);
    } catch {}
  };

  // Inline "Upload New Route PDF" inside the route section. Uses the existing
  // /api/route/validate endpoint which both stores the PDF in route_files and
  // returns a preliminary validation. After upload we re-run the cleaner
  // /route/validate-from-file so the panel shows the exact same report the
  // user is used to. No order/assignment/history mutation.
  const routeReplaceRef = React.useRef(null);
  const [replacing, setReplacing] = useState(false);
  const handleReplaceRoutePdf = async (file) => {
    if (!file) return;
    setReplacing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("inspector_name", inspectorName);
      await axios.post(`${API}/route/validate`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await fetchRouteHistory();
      // Refresh the Validate Route panel against the newest PDF.
      await handleValidateFromLatestPdf();
    } catch (err) {
      setPdfValidation({ error: err.response?.data?.detail || "Upload failed" });
    } finally {
      setReplacing(false);
      if (routeReplaceRef.current) routeReplaceRef.current.value = "";
    }
  };

  // ═══════ Day Off / Time Off ═══════
  const todayISO = new Date().toISOString().slice(0, 10);
  const [dayOffRecord, setDayOffRecord] = useState(null);
  const [dayOffHistory, setDayOffHistory] = useState([]);
  const [showDayOffModal, setShowDayOffModal] = useState(false);
  const [dayOffDate, setDayOffDate] = useState(todayISO);
  const [dayOffTime, setDayOffTime] = useState("");
  const [dayOffComment, setDayOffComment] = useState("");
  const [dayOffFile, setDayOffFile] = useState(null);
  const [dayOffLoading, setDayOffLoading] = useState(false);
  const [dayOffError, setDayOffError] = useState("");
  const dayOffFileRef = React.useRef(null);

  const fetchDayOff = useCallback(async () => {
    try {
      const [todayRes, listRes] = await Promise.all([
        axios.get(`${API}/inspector/day-off-today/${encodeURIComponent(inspectorName)}`),
        axios.get(`${API}/inspector/day-off/${encodeURIComponent(inspectorName)}`),
      ]);
      setDayOffRecord(todayRes.data?.active ? todayRes.data : null);
      setDayOffHistory(listRes.data?.records || []);
    } catch {}
  }, [inspectorName]);

  useEffect(() => { fetchDayOff(); }, [fetchDayOff]);

  const submitDayOff = async () => {
    setDayOffError("");
    if (!dayOffDate) { setDayOffError("Date is required"); return; }
    if (!dayOffFile) { setDayOffError("Proof attachment is required"); return; }
    setDayOffLoading(true);
    try {
      const fd = new FormData();
      fd.append("inspector_name", inspectorName);
      fd.append("day_off_date", dayOffDate);
      fd.append("day_off_time", dayOffTime || "");
      fd.append("comment", dayOffComment || "");
      fd.append("file", dayOffFile);
      await axios.post(`${API}/inspector/day-off`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setShowDayOffModal(false);
      setDayOffDate(todayISO); setDayOffTime(""); setDayOffComment(""); setDayOffFile(null);
      await fetchDayOff();
      setMessage({ type: "success", text: "Day Off recorded successfully" });
    } catch (e) {
      setDayOffError(e.response?.data?.detail || "Failed to record Day Off");
    } finally {
      setDayOffLoading(false);
    }
  };

  const deleteDayOff = async (id) => {
    if (!window.confirm("Delete this Day Off record?")) return;
    try {
      await axios.delete(`${API}/inspector/day-off/${id}`);
      await fetchDayOff();
      setMessage({ type: "success", text: "Day Off record removed" });
    } catch (e) {
      setMessage({ type: "error", text: e.response?.data?.detail || "Failed to delete record" });
    }
  };

  const fetchOrders = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dispatch/inspector-orders/${encodeURIComponent(inspectorName)}`);
      setOrders(res.data.orders || []);
      setArchivedOrders(res.data.archived_orders || []);
      setHoldOrders(res.data.hold_orders || []);
      setReturnedCount(res.data.returned_count || 0);
      setTotalInQa(res.data.total_in_qa || 0);
    } catch (e) {
      console.error("Failed to fetch inspector orders:", e);
    } finally {
      setLoading(false);
    }
  }, [inspectorName]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // ═══════ Available Orders (Dispatch + Scheduling) ═══════
  const [availableOrders, setAvailableOrders] = useState([]);
  const [availableLoading, setAvailableLoading] = useState(false);
  const [availableSearch, setAvailableSearch] = useState("");
  const [availableSource, setAvailableSource] = useState("all"); // all | Dispatch | Scheduling
  const [assigning, setAssigning] = useState(null); // order id being assigned
  const [showAvailable, setShowAvailable] = useState(false);

  const fetchAvailable = useCallback(async () => {
    setAvailableLoading(true);
    try {
      const res = await axios.get(`${API}/dispatch/available-for-assignment`, {
        params: { q: availableSearch, limit: 500 },
      });
      setAvailableOrders(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error("Failed to fetch available orders:", e);
    } finally {
      setAvailableLoading(false);
    }
  }, [availableSearch]);

  useEffect(() => {
    if (!showAvailable || embedded) return;
    const t = setTimeout(() => { fetchAvailable(); }, 250);
    return () => clearTimeout(t);
  }, [showAvailable, fetchAvailable, embedded]);

  const filteredAvailable = useMemo(() => {
    if (availableSource === "all") return availableOrders;
    return availableOrders.filter(o => (o.source || "Dispatch") === availableSource);
  }, [availableOrders, availableSource]);

  const assignAvailableOrder = async (order) => {
    if (!order?.id) return;
    setAssigning(order.id);
    try {
      const res = await axios.post(`${API}/dispatch/batch-assign-inspector`, {
        inspector_name: inspectorName,
        order_ids: [order.id],
        assignment_source: order.source || "Dispatch",
      });
      setMessage({
        type: "success",
        text: `Order ${order.order_number || ""} assigned to ${inspectorName} (source: ${order.source || "Dispatch"})`,
      });
      // Optimistically remove from available pool + refresh inspector's route
      setAvailableOrders(prev => prev.filter(o => o.id !== order.id));
      fetchOrders();
    } catch (e) {
      setMessage({
        type: "error",
        text: e.response?.data?.detail || "Failed to assign order",
      });
    } finally {
      setAssigning(null);
    }
  };

  const handleSort = (key) => {
    if (sortBy === key) setSortDir(prev => prev === "asc" ? "desc" : "asc");
    else { setSortBy(key); setSortDir("asc"); }
  };

  const { activeOrders, holdOrdersList } = useMemo(() => {
    // Active = Assigned, In Field, Inspection Completed, Returned (from backend 'orders')
    return { activeOrders: orders, holdOrdersList: holdOrders };
  }, [orders, holdOrders]);

  const currentTabOrders = useMemo(() => {
    if (viewTab === 'hold') return holdOrdersList;
    if (viewTab === 'qa_history') return archivedOrders;
    return activeOrders;
  }, [viewTab, activeOrders, holdOrdersList, archivedOrders]);

  const filteredOrders = useMemo(() => {
    let result = [...currentTabOrders];
    if (search) {
      const term = search.toLowerCase();
      result = result.filter(o =>
        (o.order_number || "").toLowerCase().includes(term) ||
        (o.company || "").toLowerCase().includes(term) ||
        (o.order_type || "").toLowerCase().includes(term) ||
        (o.cliente || "").toLowerCase().includes(term) ||
        (o.address || "").toLowerCase().includes(term) ||
        (o.city || "").toLowerCase().includes(term)
      );
    }
    result.sort((a, b) => {
      let aVal = a[sortBy] || "";
      let bVal = b[sortBy] || "";
      if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return result;
  }, [currentTabOrders, search, sortBy, sortDir]);

  const handleSelectToggle = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleSelectAll = (checked) => {
    if (checked) setSelectedIds(new Set(filteredOrders.map(o => o.id)));
    else setSelectedIds(new Set());
  };

  const lastClickedRef = React.useRef(null);

  const handleCheckboxChange = (order, e) => {
    if (e.nativeEvent.shiftKey && lastClickedRef.current) {
      const lastIdx = filteredOrders.findIndex(o => o.id === lastClickedRef.current);
      const curIdx = filteredOrders.findIndex(o => o.id === order.id);
      if (lastIdx !== -1 && curIdx !== -1) {
        const start = Math.min(lastIdx, curIdx);
        const end = Math.max(lastIdx, curIdx);
        setSelectedIds(prev => {
          const next = new Set(prev);
          filteredOrders.slice(start, end + 1).forEach(o => next.add(o.id));
          return next;
        });
        lastClickedRef.current = order.id;
        return;
      }
    }
    lastClickedRef.current = order.id;
    handleSelectToggle(order.id);
  };

  const handleQuickSelect = (count) => {
    setSelectedIds(new Set(filteredOrders.slice(0, count).map(o => o.id)));
  };

  const allSelected = filteredOrders.length > 0 && filteredOrders.every(o => selectedIds.has(o.id));

  // Transmission modal state
  const [showTransmitModal, setShowTransmitModal] = useState(false);
  const [transmitOperator, setTransmitOperator] = useState('');
  const [transmitPaidBy, setTransmitPaidBy] = useState('');
  const [officeReason, setOfficeReason] = useState('');
  const [officeReasonDetail, setOfficeReasonDetail] = useState('');

  const handleBatchTransmit = () => {
    if (selectedIds.size === 0) return;
    setShowTransmitModal(true);
  };

  const handleConfirmTransmit = async () => {
    if (!transmitOperator || !transmitPaidBy) return;
    if (transmitPaidBy === 'Office' && !officeReason) return;
    if (transmitPaidBy === 'Office' && officeReason === 'Other' && !officeReasonDetail.trim()) return;

    setProcessing("transmit");
    try {
      const res = await axios.post(`${API}/dispatch/batch-transmit-office`, {
        order_ids: Array.from(selectedIds),
        transmitted_by_operator: transmitOperator,
        paid_by: transmitPaidBy,
        office_reason: transmitPaidBy === 'Office' ? officeReason : null,
        office_reason_detail: transmitPaidBy === 'Office' && officeReason === 'Other' ? officeReasonDetail : null,
      });
      const paidLabel = res.data.paid_by === 'Office' ? ' (Office-paid)' : '';
      setMessage({ type: "success", text: `${res.data.transmitted} orders transmitted by ${transmitOperator}${paidLabel}` });
      setSelectedIds(new Set());
      setShowTransmitModal(false);
      setTransmitOperator(''); setTransmitPaidBy(''); setOfficeReason(''); setOfficeReasonDetail('');
      fetchOrders();
    } catch (e) {
      setMessage({ type: "error", text: e.response?.data?.detail || "Failed to transmit" });
    } finally {
      setProcessing(null);
    }
  };

  const handleBatchClose = () => {
    if (selectedIds.size === 0) return;
    setShowCloseModal(true);
  };

  const columns = [
    { key: "due_date", label: "Due Date", sortable: true },
    { key: "order_number", label: "Order #", sortable: true },
    { key: "company", label: "Company", sortable: true },
    { key: "order_type", label: "Type", sortable: true },
    { key: "cliente", label: "Client", sortable: true },
    { key: "address", label: "Address", sortable: true },
    { key: "city", label: "City", sortable: true },
    { key: "state", label: "State", sortable: false },
    { key: "status", label: "Status", sortable: true },
  ];

  const transmittedCount = archivedOrders.filter(o => o.transmitted === "yes" || o.status === "QA").length;
  const closedCount = orders.filter(o => o.daily_closed === "yes").length;
  const pendingCount = orders.filter(o => o.daily_closed !== "yes" && o.transmitted !== "yes").length;

  return (
    <div className="space-y-5" data-testid="inspector-orders-page">
      {/* Header — hidden when embedded */}
      {!embedded && (
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-[#1F2937] transition-colors"
            data-testid="inspector-orders-back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <User className="h-5 w-5 text-[#B08A3E]" />
              {inspectorName}
            </h1>
            <p className="text-gray-500 text-sm">{activeOrders.length} active orders{!embedded && totalInQa > 0 && <span className="text-gray-400"> &middot; {totalInQa} in QA</span>}</p>
          </div>
        </div>
      </div>
      )}

      {/* Inspector Field Training Buttons — HIDDEN entirely on this page
          per user spec (Apr 21 2026). Foundation / Roof / Occupancy
          category buttons belong on the Inspector's own screen, not on
          the Dispatch inspector detail view nor embedded profile view.
          Toggle false→true to restore later if needed. */}
      {false && <InspectorTrainingButtons />}

      {/* Summary Stats — ONLY visible when embedded in Dispatch */}
      {embedded && (
      <div className="flex items-center gap-3 flex-wrap">
        <div className="grid grid-cols-2 gap-3 flex-1">
          <div className="bg-white rounded-lg border border-[#D1A054]/30 px-4 py-3">
            <p className="text-[10px] uppercase text-[#D1A054] font-semibold tracking-wider">Active</p>
            <p className="text-2xl font-bold text-[#B08A3E] mt-0.5" data-testid="inspector-active-count">{activeOrders.length}</p>
          </div>
          {returnedCount > 0 && (
            <div className="bg-white rounded-lg border border-[#C07070]/30 px-4 py-3">
              <p className="text-[10px] uppercase text-[#C07070] font-semibold tracking-wider">Returned</p>
              <p className="text-2xl font-bold text-[#C07070] mt-0.5" data-testid="inspector-returned-count">{returnedCount}</p>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Day Off / Time Off Banner */}
      {dayOffRecord && (
        <div className="bg-gradient-to-r from-[#F4E8D1] to-[#EDE5D6] border-2 border-[#D1A054]/50 rounded-lg p-4 flex items-start gap-3 shadow-sm" data-testid="day-off-banner">
          <div className="p-2 rounded-full bg-[#D1A054]/20 flex-shrink-0">
            <Coffee className="h-5 w-5 text-[#B08A3E]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-bold text-[#7A6535] uppercase tracking-wide">Day Off Recorded</h3>
              <span className="text-[10px] px-2 py-0.5 bg-[#B08A3E]/15 text-[#7A6535] rounded-full font-semibold" data-testid="day-off-date">
                {dayOffRecord.day_off_date}{dayOffRecord.day_off_time ? ` · ${dayOffRecord.day_off_time}` : ""}
              </span>
            </div>
            {dayOffRecord.comment && (
              <p className="text-xs text-[#7A6535] mt-1.5 italic" data-testid="day-off-comment">"{dayOffRecord.comment}"</p>
            )}
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              {dayOffRecord.proof_url && (
                <a href={`${process.env.REACT_APP_BACKEND_URL}${dayOffRecord.proof_url}`} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white text-[#2F5D8A] text-[11px] font-semibold rounded border border-[#2F5D8A]/20 hover:bg-[#2F5D8A]/5"
                  data-testid="day-off-proof-link">
                  <Paperclip className="h-3 w-3" /> View Proof
                </a>
              )}
              <span className="text-[10px] text-gray-500">
                Recorded by {dayOffRecord.created_by}{dayOffRecord.created_at ? ` · ${new Date(dayOffRecord.created_at).toLocaleString()}` : ""}
              </span>
              <button onClick={() => deleteDayOff(dayOffRecord.id)}
                className="ml-auto inline-flex items-center gap-1 px-2 py-1 text-[10px] text-[#DC2626] hover:bg-[#DC2626]/10 rounded font-medium"
                data-testid="day-off-delete-btn">
                <Trash2 className="h-3 w-3" /> Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Route Validation Section — restored per user spec (Apr 21 2026).
          Required for route/address comparison workflow. */}
      <div className="space-y-2" data-testid="route-validation-section">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => {
              if (showRouteSection) {
                setShowRouteSection(false);
              } else {
                handleValidateFromLatestPdf();
              }
            }}
            disabled={pdfValidationLoading}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${showRouteSection ? "bg-[#2F5D8A] text-white" : "bg-[#2F5D8A]/8 text-[#2F5D8A] hover:bg-[#2F5D8A]/15"} disabled:opacity-50`}
            data-testid="route-toggle-btn">
            <MapPin className="h-3.5 w-3.5" />
            {pdfValidationLoading ? "Validating..." : (showRouteSection ? "Hide" : "Validate")} Route
            {pdfValidation?.matched_total !== undefined && !pdfValidation?.error && (
              <span className="ml-1 px-1.5 py-0.5 bg-white/20 rounded text-[9px]">
                {pdfValidation.matched_total}/{pdfValidation.pdf_addresses_total}
              </span>
            )}
          </button>
          <button onClick={() => setShowDayOffModal(true)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold bg-[#D1A054]/10 text-[#B08A3E] hover:bg-[#D1A054]/20 transition-all"
            data-testid="day-off-open-btn">
            <Coffee className="h-3.5 w-3.5" /> Mark Day Off
            {dayOffHistory.length > 0 && (
              <span className="ml-0.5 px-1.5 py-0.5 bg-[#D1A054]/25 text-[#7A6535] rounded text-[9px] font-bold">{dayOffHistory.length}</span>
            )}
          </button>
          <button onClick={() => setShowReviewModal(true)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold bg-[#2F5D8A]/10 text-[#2F5D8A] hover:bg-[#2F5D8A]/20 transition-all"
            data-testid="open-daily-close-review-btn"
            title="Open Daily Close Review for this inspector + today's route date">
            <FileText className="h-3.5 w-3.5" /> Daily Close Review
          </button>
          {/* Route-file indicator removed per user spec (Apr 21 2026) —
              file controls now live inside the Validate Route report panel
              so Delete / Upload New are in one consolidated place. */}
        </div>

        {/* ═════ Route Validation Report — built from the latest PDF already
            attached to this inspector. Pure comparison, report-only. ═════ */}
        {showRouteSection && pdfValidation && (
          <div className="bg-white rounded-xl border border-[#2F5D8A]/20 p-4 space-y-3" data-testid="route-validation-report">
            {pdfValidation.error ? (
              <div className="p-3 bg-[#F5EDED] border border-[#C07070]/30 rounded-lg flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm text-[#8B4545] flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  {pdfValidation.error || "No route PDF attached for this inspector yet."}
                </div>
                {/* Empty-state: expose the same Upload PDF control so office
                    can attach the right file without leaving Validate Route. */}
                <div className="flex items-center gap-1.5">
                  <input
                    ref={routeReplaceRef}
                    type="file"
                    accept=".pdf"
                    className="hidden"
                    onChange={(e) => handleReplaceRoutePdf(e.target.files?.[0])}
                    data-testid="route-upload-pdf-input-empty"
                  />
                  <button
                    onClick={() => routeReplaceRef.current?.click()}
                    disabled={replacing}
                    className="px-3 py-1.5 rounded-md text-[11px] font-semibold bg-[#2F5D8A] text-white hover:bg-[#24496e] disabled:opacity-50 flex items-center gap-1.5"
                    data-testid="route-upload-pdf-btn-empty"
                    title="Upload the route PDF for this inspector (validation only)"
                  >
                    <Upload className="h-3.5 w-3.5" />
                    {replacing ? "Uploading…" : "Upload PDF"}
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Source pill */}
                <div className="flex flex-wrap items-center gap-2 text-[11px] text-gray-500">
                  <FileText className="h-3.5 w-3.5" />
                  <span>Source: <span className="font-semibold text-[#1F2937]">{pdfValidation.source_file}</span></span>
                  {pdfValidation.source_date && <span className="text-gray-400">· {pdfValidation.source_date}</span>}
                  <div className="ml-auto flex items-center gap-1.5">
                    <button
                      onClick={handleValidateFromLatestPdf}
                      disabled={pdfValidationLoading}
                      className="px-2 py-0.5 rounded text-[10px] font-semibold bg-[#2F5D8A]/10 text-[#2F5D8A] hover:bg-[#2F5D8A]/20 disabled:opacity-50"
                      data-testid="route-validation-refresh"
                    >
                      Re-run
                    </button>
                    {/* ═══ Delete wrong PDF + Upload new PDF (Apr 21 2026) ═══
                        Only affects the route_files record. Never touches
                        orders, assignments, GPX, finance, or Daily Close. */}
                    <input
                      ref={routeReplaceRef}
                      type="file"
                      accept=".pdf"
                      className="hidden"
                      onChange={(e) => handleReplaceRoutePdf(e.target.files?.[0])}
                      data-testid="route-replace-file-input"
                    />
                    <button
                      onClick={() => routeReplaceRef.current?.click()}
                      disabled={replacing || pdfValidationLoading}
                      className="px-2 py-0.5 rounded text-[10px] font-semibold bg-[#6B8F62]/12 text-[#4A7A56] hover:bg-[#6B8F62]/25 disabled:opacity-50 flex items-center gap-1"
                      data-testid="route-upload-new-btn"
                      title="Replace the current PDF with a new one (validation only)"
                    >
                      {replacing ? "Uploading…" : "Replace PDF"}
                    </button>
                    {routeHistory.length > 0 && (
                      <button
                        onClick={() => deleteRouteFile(routeHistory[0].stored_filename)}
                        disabled={replacing || pdfValidationLoading}
                        className="px-2 py-0.5 rounded text-[10px] font-semibold bg-[#C07070]/15 text-[#8B4545] hover:bg-[#C07070]/25 disabled:opacity-50 flex items-center gap-1"
                        data-testid="route-delete-current-btn"
                        title="Remove this route PDF only. Does not touch orders, assignments, GPX, finance, or Daily Close."
                      >
                        Remove PDF
                      </button>
                    )}
                  </div>
                </div>

                {/* ═══════════════════════════════════════════════════════
                    Success banner (Apr 22 2026) — shown the moment the
                    three reconciliation conditions are all satisfied:
                      matched == pdf_total  AND
                      missing == 0  AND
                      extra == 0
                    Gives the dispatcher an instant visual confirmation that
                    the route is fully reconciled. No action; report-only.
                    ═══════════════════════════════════════════════════ */}
                {(() => {
                  const pdfT = pdfValidation.pdf_addresses_total || 0;
                  const matT = pdfValidation.matched_total || 0;
                  const misT = pdfValidation.missing_in_account_total || 0;
                  const extT = pdfValidation.extra_in_account_total || 0;
                  const isAllSet = pdfT > 0 && matT === pdfT && misT === 0 && extT === 0;
                  if (!isAllSet) return null;
                  return (
                    <div
                      className="flex items-center gap-2 p-2.5 rounded-lg bg-[#6B8F62]/10 border border-[#6B8F62]/40 mb-2"
                      data-testid="route-validation-all-set"
                    >
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#6B8F62] text-white text-[13px] font-bold shrink-0">
                        ✓
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-[#4A7A56] leading-tight">
                          All Set · Route Validated
                        </p>
                        <p className="text-[11px] text-[#4A7A56]/80 leading-tight mt-0.5">
                          Every PDF stop matches an assigned order. 0 missing · 0 extra.
                        </p>
                      </div>
                    </div>
                  );
                })()}

                {/* Top summary */}
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-center">
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-2">
                    <p className="text-[9px] uppercase tracking-wider text-gray-500 font-semibold">PDF Addresses</p>
                    <p className="text-lg font-bold text-[#1F2937]">{pdfValidation.pdf_addresses_total}</p>
                    {pdfValidation.pdf_breakdown && (
                      <p className="text-[9px] text-gray-400 leading-tight mt-0.5">
                        {pdfValidation.pdf_breakdown.start}+{pdfValidation.pdf_breakdown.stops}+{pdfValidation.pdf_breakdown.end}
                        <br/>start · stops · end
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => setPdfValidationSection("matched")}
                    className="bg-gray-50 border border-gray-200 rounded-lg p-2 hover:bg-gray-100 text-center transition-all"
                    title="Click to see matched + extra orders"
                    data-testid="route-validation-tab-assigned"
                  >
                    <p className="text-[9px] uppercase tracking-wider text-gray-500 font-semibold">Assigned</p>
                    <p className="text-lg font-bold text-[#1F2937]">{pdfValidation.system_orders_total}</p>
                    <p className="text-[9px] text-gray-400 leading-tight mt-0.5">{pdfValidation.matched_total} match<br/>+{pdfValidation.extra_in_account_total} extra</p>
                  </button>
                  <button
                    onClick={() => setPdfValidationSection("matched")}
                    className={`rounded-lg p-2 border text-center transition-all ${pdfValidationSection === "matched" ? "border-[#6B8F62] bg-[#6B8F62]/12 ring-1 ring-[#6B8F62]/30" : "border-[#6B8F62]/25 bg-[#6B8F62]/5 hover:bg-[#6B8F62]/10"}`}
                    data-testid="route-validation-tab-matched"
                  >
                    <p className="text-[9px] uppercase tracking-wider text-[#6B8F62] font-semibold">Matched</p>
                    <p className="text-lg font-bold text-[#6B8F62]">{pdfValidation.matched_total}</p>
                  </button>
                  <button
                    onClick={() => setPdfValidationSection("missing")}
                    className={`rounded-lg p-2 border text-center transition-all ${pdfValidationSection === "missing" ? "border-[#DC2626] bg-[#DC2626]/10 ring-1 ring-[#DC2626]/30" : "border-[#DC2626]/25 bg-[#DC2626]/5 hover:bg-[#DC2626]/10"}`}
                    data-testid="route-validation-tab-missing"
                  >
                    <p className="text-[9px] uppercase tracking-wider text-[#DC2626] font-semibold">Missing</p>
                    <p className="text-lg font-bold text-[#DC2626]">{pdfValidation.missing_in_account_total}</p>
                  </button>
                  <button
                    onClick={() => setPdfValidationSection("extra")}
                    className={`rounded-lg p-2 border text-center transition-all ${pdfValidationSection === "extra" ? "border-[#D97706] bg-[#D97706]/10 ring-1 ring-[#D97706]/30" : "border-[#D97706]/25 bg-[#D97706]/5 hover:bg-[#D97706]/10"}`}
                    data-testid="route-validation-tab-extra"
                  >
                    <p className="text-[9px] uppercase tracking-wider text-[#D97706] font-semibold">Extra</p>
                    <p className="text-lg font-bold text-[#D97706]">{pdfValidation.extra_in_account_total}</p>
                  </button>
                </div>

                {/* Results list */}
                <div className="max-h-[360px] overflow-y-auto border border-gray-100 rounded-lg" data-testid="route-validation-list">
                  {pdfValidationSection === "matched" && (
                    pdfValidation.matched.length === 0 ? (
                      <p className="p-4 text-xs text-gray-400 italic text-center">No matched addresses.</p>
                    ) : (
                      <table className="w-full text-xs">
                        <thead className="bg-[#6B8F62]/10 sticky top-0">
                          <tr>
                            <th className="text-left px-2 py-1.5 font-semibold text-[#6B8F62]">Order #</th>
                            <th className="text-left px-2 py-1.5 font-semibold text-[#6B8F62]">Address</th>
                            <th className="text-left px-2 py-1.5 font-semibold text-[#6B8F62]">City/State</th>
                            <th className="text-left px-2 py-1.5 font-semibold text-[#6B8F62]">Status</th>
                            <th className="text-left px-2 py-1.5 font-semibold text-[#6B8F62]">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pdfValidation.matched.map((m, i) => (
                            <tr key={i} className="border-t border-gray-50 hover:bg-[#6B8F62]/5">
                              <td className="px-2 py-1.5 font-mono text-[#1F2937]">{m.order_number}</td>
                              <td className="px-2 py-1.5 text-[#1F2937]">
                                {m.role === "start" && <span className="inline-block px-1.5 py-0.5 mr-1.5 rounded text-[9px] font-bold bg-[#6B8F62] text-white">START</span>}
                                {m.role === "end" && <span className="inline-block px-1.5 py-0.5 mr-1.5 rounded text-[9px] font-bold bg-[#DC2626] text-white">END</span>}
                                {m.address}
                              </td>
                              <td className="px-2 py-1.5 text-gray-500">{m.city}{m.state ? `, ${m.state}` : ""}</td>
                              <td className="px-2 py-1.5"><span className="px-1.5 py-0.5 rounded bg-gray-100 text-[10px]">{m.status}</span></td>
                              <td className="px-2 py-1.5">
                                <button
                                  onClick={() => m.id && navigate(`/order/${m.id}`)}
                                  className="px-2 py-0.5 rounded bg-[#6B8F62]/20 text-[#6B8F62] text-[10px] font-semibold hover:bg-[#6B8F62]/30"
                                  title="Open order detail"
                                >
                                  Open
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )
                  )}
                  {pdfValidationSection === "missing" && (
                    pdfValidation.missing_in_account.length === 0 ? (
                      <p className="p-4 text-xs text-gray-400 italic text-center">No missing addresses. Great!</p>
                    ) : (
                      <div className="divide-y divide-gray-50">
                        {pdfValidation.missing_in_account.map((m, i) => {
                          const suggested = m.suggested_order;
                          const hasSuggestion = !!(suggested && suggested.id);
                          const looseMatch = hasSuggestion && suggested.loose_match;
                          return (
                          <div key={i} className="px-3 py-2 hover:bg-[#DC2626]/5 flex items-center justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-[#DC2626]">
                                {m.role === "start" && <span className="inline-block px-1.5 py-0.5 mr-1.5 rounded text-[9px] font-bold bg-[#6B8F62] text-white">START</span>}
                                {m.role === "end" && <span className="inline-block px-1.5 py-0.5 mr-1.5 rounded text-[9px] font-bold bg-[#DC2626] text-white">END</span>}
                                {m.pdf_address}
                              </p>
                              {hasSuggestion ? (
                                <p className="text-[10px] text-gray-500 mt-0.5">
                                  {looseMatch
                                    ? <span className="text-[#B45309] font-semibold">Number-only match · street name differs:</span>
                                    : <span>Match found:</span>}
                                  &nbsp;
                                  <span className="font-semibold text-[#1F2937]">#{suggested.order_number}</span>
                                  <span className="ml-1 text-gray-600">{suggested.address}{suggested.city ? ` · ${suggested.city}` : ""}</span>
                                  {suggested.assigned_inspector
                                    ? <span className="ml-1 text-[#B45309]">· currently {suggested.assigned_inspector}</span>
                                    : <span className="ml-1 text-[#6B8F62]">· unassigned</span>}
                                </p>
                              ) : (
                                <p className="text-[10px] text-gray-400 mt-0.5 italic">No order with this street number exists in the system.</p>
                              )}
                            </div>
                            {hasSuggestion ? (
                              <button
                                onClick={() => assignMissingToInspector(suggested.id, suggested.order_number)}
                                disabled={rowBusyId === suggested.id}
                                className={`px-2.5 py-1 rounded text-white text-[10px] font-semibold shrink-0 disabled:opacity-50 ${looseMatch ? "bg-[#B45309] hover:bg-[#92400E]" : "bg-[#2F5D8A] hover:bg-[#264A6E]"}`}
                                title={looseMatch
                                  ? `Street name differs. Assigning #${suggested.order_number} (${suggested.address}) to ${inspectorName}.`
                                  : `Assign order #${suggested.order_number} to ${inspectorName} (no page reload)`}
                                data-testid={`validate-route-missing-assign-${i}`}
                              >
                                {rowBusyId === suggested.id ? "Assigning…" : "Assign to Inspector →"}
                              </button>
                            ) : (
                              <span
                                className="px-2.5 py-1 rounded bg-gray-100 text-gray-400 text-[10px] font-semibold shrink-0"
                                title="Nothing to assign — no order with this street number exists in the system."
                                data-testid={`validate-route-missing-none-${i}`}
                              >
                                No match
                              </span>
                            )}
                          </div>
                          );
                        })}
                      </div>
                    )
                  )}
                  {pdfValidationSection === "extra" && (
                    pdfValidation.extra_in_account.length === 0 ? (
                      <p className="p-4 text-xs text-gray-400 italic text-center">No extras. Route is tight.</p>
                    ) : (
                      <table className="w-full text-xs">
                        <thead className="bg-[#D97706]/10 sticky top-0">
                          <tr>
                            <th className="text-left px-2 py-1.5 font-semibold text-[#D97706]">Order #</th>
                            <th className="text-left px-2 py-1.5 font-semibold text-[#D97706]">Address</th>
                            <th className="text-left px-2 py-1.5 font-semibold text-[#D97706]">City/State</th>
                            <th className="text-left px-2 py-1.5 font-semibold text-[#D97706]">Status</th>
                            <th className="text-left px-2 py-1.5 font-semibold text-[#D97706]">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pdfValidation.extra_in_account.map((m, i) => (
                            <tr key={i} className="border-t border-gray-50 hover:bg-[#D97706]/5">
                              <td className="px-2 py-1.5 font-mono text-[#1F2937]">{m.order_number}</td>
                              <td className="px-2 py-1.5 text-[#1F2937]">{m.address}</td>
                              <td className="px-2 py-1.5 text-gray-500">{m.city}{m.state ? `, ${m.state}` : ""}</td>
                              <td className="px-2 py-1.5"><span className="px-1.5 py-0.5 rounded bg-gray-100 text-[10px]">{m.status}</span></td>
                              <td className="px-2 py-1.5">
                                <button
                                  onClick={() => unassignExtraOrder(m.id, m.order_number)}
                                  disabled={!m.id || rowBusyId === m.id}
                                  className="px-2 py-0.5 rounded bg-[#C07070]/15 text-[#8B4545] text-[10px] font-semibold hover:bg-[#C07070]/25 disabled:opacity-50"
                                  title={`Unassign this order from ${inspectorName}. Order stays in system.`}
                                  data-testid={`validate-route-extra-unassign-${i}`}
                                >
                                  {rowBusyId === m.id ? "Unassigning…" : "Unassign Order →"}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )
                  )}
                </div>

                <p className="text-[9px] text-gray-400 text-center italic">Report-only tool — nothing is auto-assigned, removed, or modified.</p>
              </>
            )}
          </div>
        )}

        {/* Legacy upload-based panel — kept for manual file uploads. */}
        {false && showRouteSection && (
          <div className="bg-white rounded-lg border border-[#2F5D8A]/20 p-4 space-y-3">
            <div className="flex items-center gap-3">
              <div className={`flex-1 border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition-all ${routeFile ? "border-[#6B8F62] bg-[#6B8F62]/5" : "border-gray-300 hover:border-[#2F5D8A]"}`}
                onClick={() => routeFileRef.current?.click()} data-testid="route-dropzone">
                <input ref={routeFileRef} type="file" accept=".gpx,.xlsx,.xls,.csv,.pdf" className="hidden"
                  onChange={e => { setRouteFile(e.target.files[0]); setRouteResult(null); setRouteExpanded(null); }} data-testid="route-file-input" />
                {routeFile ? (
                  <span className="text-xs font-medium text-[#6B8F62] flex items-center justify-center gap-1.5"><FileText className="h-4 w-4" /> {routeFile.name}</span>
                ) : (
                  <span className="text-xs text-gray-400 flex flex-col items-center justify-center gap-0.5">
                    <span className="flex items-center gap-1.5"><Upload className="h-4 w-4" /> Upload route (.gpx, .pdf, .xlsx, .csv)</span>
                    <span className="text-[9px] text-gray-400">GPX → powers map · PDF → audit / validation only</span>
                  </span>
                )}
              </div>
              <button onClick={handleRouteValidation} disabled={!routeFile || routeLoading}
                className={`px-4 py-2.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 ${!routeFile || routeLoading ? "bg-gray-200 text-gray-400" : "bg-[#2F5D8A] text-white hover:bg-[#24496e]"}`}
                data-testid="route-validate-btn">
                {routeLoading ? <div className="animate-spin h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full" /> : <MapPin className="h-3.5 w-3.5" />}
                {routeLoading ? "Checking..." : "Validate"}
              </button>
            </div>

            {routeResult?.error && <div className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-600">{routeResult.error}</div>}

            {routeResult && !routeResult.error && (
              <>
                <div className="grid grid-cols-3 gap-2">
                  <button onClick={() => setRouteExpanded(routeExpanded === "matched" ? null : "matched")}
                    className={`p-2.5 rounded-lg border text-left cursor-pointer transition-all ${routeExpanded === "matched" ? "border-[#6B8F62] ring-1 ring-[#6B8F62]" : "border-gray-200 hover:shadow"}`}
                    data-testid="route-card-matched">
                    <p className="text-[9px] uppercase font-bold text-[#6B8F62]">Matched</p>
                    <p className="text-xl font-bold text-[#6B8F62]">{routeResult.matched}</p>
                    <p className="text-[8px] text-gray-400">system & route</p>
                  </button>
                  <button onClick={() => setRouteExpanded(routeExpanded === "missing" ? null : "missing")}
                    className={`p-2.5 rounded-lg border text-left cursor-pointer transition-all ${routeExpanded === "missing" ? "border-[#DC2626] ring-1 ring-[#DC2626]" : "border-gray-200 hover:shadow"} ${routeResult.missing_in_route > 0 ? "bg-[#DC2626]/5" : ""}`}
                    data-testid="route-card-missing">
                    <p className="text-[9px] uppercase font-bold text-[#DC2626]">Missing in Route</p>
                    <p className="text-xl font-bold text-[#DC2626]">{routeResult.missing_in_route}</p>
                    <p className="text-[8px] text-gray-400">may miss these</p>
                  </button>
                  <button onClick={() => setRouteExpanded(routeExpanded === "extra" ? null : "extra")}
                    className={`p-2.5 rounded-lg border text-left cursor-pointer transition-all ${routeExpanded === "extra" ? "border-[#D97706] ring-1 ring-[#D97706]" : "border-gray-200 hover:shadow"} ${routeResult.extra_in_route > 0 ? "bg-[#D97706]/5" : ""}`}
                    data-testid="route-card-extra">
                    <p className="text-[9px] uppercase font-bold text-[#D97706]">Extra in Route</p>
                    <p className="text-xl font-bold text-[#D97706]">{routeResult.extra_in_route}</p>
                    <p className="text-[8px] text-gray-400">not in system</p>
                  </button>
                </div>
                <p className="text-[9px] text-gray-400 text-center">Route: {routeResult.route_entries} entries | System: {routeResult.system_orders} orders assigned</p>

                {routeExpanded && (
                  <div className="rounded-lg border border-gray-200 overflow-hidden">
                    <div className={`px-3 py-1.5 text-[10px] font-bold uppercase ${routeExpanded === "matched" ? "bg-[#6B8F62]/10 text-[#6B8F62]" : routeExpanded === "missing" ? "bg-[#DC2626]/10 text-[#DC2626]" : "bg-[#D97706]/10 text-[#D97706]"}`}>
                      {routeExpanded === "matched" ? `Matched (${routeResult.matched})` : routeExpanded === "missing" ? `Missing in Route (${routeResult.missing_in_route})` : `Extra in Route (${routeResult.extra_in_route})`}
                    </div>
                    <div className="max-h-[200px] overflow-y-auto">
                      <table className="w-full text-[10px]">
                        <thead className="sticky top-0 bg-gray-50"><tr className="border-b">
                          <th className="px-2 py-1 text-left font-semibold text-gray-500">Order #</th>
                          <th className="px-2 py-1 text-left font-semibold text-gray-500">Address</th>
                          <th className="px-2 py-1 text-left font-semibold text-gray-500">City</th>
                          <th className="px-2 py-1 text-left font-semibold text-gray-500">Status</th>
                        </tr></thead>
                        <tbody>{(routeExpanded === "matched" ? routeResult.matched_list : routeExpanded === "missing" ? routeResult.missing_list : routeResult.extra_list).map((o, i) => (
                          <tr key={i} className={`border-b border-gray-50 ${routeExpanded === "missing" ? "bg-[#DC2626]/[0.03]" : routeExpanded === "extra" ? "bg-[#D97706]/[0.03]" : ""}`}>
                            <td className="px-2 py-1 font-medium text-gray-700">{o.order_number || "-"}</td>
                            <td className="px-2 py-1 text-gray-500">{o.address || o.route_raw || "-"}</td>
                            <td className="px-2 py-1 text-gray-400">{o.city || "-"}</td>
                            <td className="px-2 py-1 text-gray-400">{o.status || "-"}</td>
                          </tr>
                        ))}</tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Route File History — split into Today + Archive (past days) */}
            {routeHistory.length > 0 && (() => {
              const todayStr = new Date().toISOString().slice(0, 10);
              const isToday = (rf) => {
                const d = (rf.route_date || "") || (rf.upload_date ? rf.upload_date.slice(0, 10) : "");
                return d === todayStr;
              };
              const todayFiles = routeHistory.filter(isToday);
              const archiveFiles = routeHistory.filter(rf => !isToday(rf));
              const renderRow = (rf, i, keyPrefix) => (
                <div key={`${keyPrefix}-${i}`} className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg border border-gray-100 text-xs">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="h-3.5 w-3.5 text-[#2F5D8A] flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-gray-700 font-medium truncate">
                        {rf.original_filename}
                        {rf.file_type && (
                          <span className={`ml-1.5 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${
                            rf.file_type === "gpx" ? "bg-[#6B8F62]/15 text-[#4F7046]"
                            : rf.file_type === "pdf" ? "bg-[#C07070]/15 text-[#A15252]"
                            : "bg-gray-200 text-gray-600"
                          }`}>{rf.file_type === "gpx" ? "GPX · MAP" : rf.file_type === "pdf" ? "PDF · AUDIT" : rf.file_type}</span>
                        )}
                      </p>
                      <p className="text-[9px] text-gray-400">
                        {rf.route_date || (rf.upload_date ? rf.upload_date.slice(0, 10) : "")} — by {rf.uploaded_by}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <a href={`${API}/route/download/${rf.stored_filename}`} target="_blank" rel="noopener noreferrer"
                      className="px-2 py-1 bg-[#2F5D8A]/8 text-[#2F5D8A] rounded text-[10px] font-medium hover:bg-[#2F5D8A]/15"
                      data-testid={`${keyPrefix}-download-${i}`}>
                      Download
                    </a>
                    <button onClick={() => deleteRouteFile(rf.stored_filename)}
                      className="p-1 text-gray-300 hover:text-[#DC2626] hover:bg-[#DC2626]/8 rounded transition-colors"
                      title="Delete route file" data-testid={`${keyPrefix}-delete-${i}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              );
              return (
                <div data-testid="route-history">
                  {todayFiles.length > 0 && (
                    <>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Today's Route Files</p>
                      <div className="space-y-1">
                        {todayFiles.map((rf, i) => renderRow(rf, i, "route-today"))}
                      </div>
                    </>
                  )}
                  {archiveFiles.length > 0 && (
                    <details className="mt-2 group" data-testid="route-history-archive">
                      <summary className="cursor-pointer list-none flex items-center gap-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider py-1 hover:text-gray-600">
                        <ChevronRight className="h-3 w-3 transition-transform group-open:rotate-90" />
                        Archive — Past Route Files ({archiveFiles.length})
                        <span className="ml-auto text-[9px] normal-case font-normal text-gray-400">moves to Daily Close at end of day</span>
                      </summary>
                      <div className="space-y-1 mt-1.5 pl-2 border-l-2 border-gray-100">
                        {archiveFiles.map((rf, i) => renderRow(rf, i, "route-archive"))}
                      </div>
                    </details>
                  )}
                  {todayFiles.length === 0 && archiveFiles.length === 0 && (
                    <p className="text-[10px] text-gray-400 italic">No route files yet.</p>
                  )}
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Route Map (shows when active route exists) */}
      <RouteMapPanel inspectorName={inspectorName} />

      {/* ═══════ Available Orders (Dispatch + Scheduling) — REMOVED per user
          spec (Apr 21 2026). Was a yellow collapsible bar showing unassigned
          orders so Dispatch could reassign from the inspector's page.
          Office now uses the dedicated "All Company Orders" button in the
          page header for that flow. Keeping the `false &&` guard so the
          feature can be restored quickly if needed. */}
      {false && !embedded && (
        <div className="bg-white rounded-xl border border-[#D1A054]/30 shadow-sm" data-testid="available-orders-panel">
          <button
            type="button"
            onClick={() => setShowAvailable(s => !s)}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#D1A054]/5 transition-colors rounded-xl"
            data-testid="available-orders-toggle"
          >
            <div className="flex items-center gap-2">
              <Search className="h-4 w-4 text-[#B08A3E]" />
              <span className="text-sm font-semibold text-[#1F2937]">Available Orders (Dispatch + Scheduling)</span>
              {availableOrders.length > 0 && (
                <span className="px-2 py-0.5 rounded-full bg-[#D1A054]/15 text-[#B08A3E] text-[10px] font-bold">
                  {availableOrders.length} ready
                </span>
              )}
            </div>
            {showAvailable ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
          </button>

          {showAvailable && (
            <div className="px-4 pb-4 space-y-3 border-t border-gray-100 pt-3">
              {/* Search + Source filter */}
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-[200px] max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search # / address / city / type / company..."
                    value={availableSearch}
                    onChange={(e) => setAvailableSearch(e.target.value)}
                    className="w-full pl-9 pr-3 py-1.5 bg-white border border-gray-300 rounded text-sm text-[#1F2937] placeholder-gray-400 focus:border-[#D1A054] focus:outline-none"
                    data-testid="available-orders-search"
                  />
                </div>
                <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
                  {[
                    { key: "all", label: "All", count: availableOrders.length },
                    { key: "Dispatch", label: "Dispatch", count: availableOrders.filter(o => o.source === "Dispatch").length },
                    { key: "Scheduling", label: "Scheduling", count: availableOrders.filter(o => o.source === "Scheduling").length },
                  ].map(f => (
                    <button
                      key={f.key}
                      onClick={() => setAvailableSource(f.key)}
                      className={`px-2.5 py-1 rounded-md text-[10px] font-semibold transition-all ${availableSource === f.key ? "bg-white text-[#1F2937] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                      data-testid={`available-filter-${f.key}`}
                    >
                      {f.label} <span className="ml-1 text-[9px] font-normal text-gray-400">({f.count})</span>
                    </button>
                  ))}
                </div>
                <button
                  onClick={fetchAvailable}
                  disabled={availableLoading}
                  className="px-2 py-1 text-xs text-[#2F5D8A] hover:bg-[#2F5D8A]/8 rounded flex items-center gap-1 disabled:opacity-40"
                  data-testid="available-orders-refresh"
                >
                  <RotateCcw className={`h-3 w-3 ${availableLoading ? "animate-spin" : ""}`} />
                  Refresh
                </button>
              </div>

              {/* Table */}
              <div className="max-h-[380px] overflow-auto border border-gray-100 rounded-lg">
                {availableLoading ? (
                  <div className="py-8 text-center text-xs text-gray-400">Loading available orders...</div>
                ) : filteredAvailable.length === 0 ? (
                  <div className="py-8 text-center text-xs text-gray-400">
                    No ready orders{availableSearch ? ` match "${availableSearch}"` : " available for assignment"}.
                  </div>
                ) : (
                  <table className="w-full text-xs" data-testid="available-orders-table">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr className="border-b border-gray-100">
                        <th className="text-left px-2 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Source</th>
                        <th className="text-left px-2 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Due</th>
                        <th className="text-left px-2 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Order #</th>
                        <th className="text-left px-2 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Co.</th>
                        <th className="text-left px-2 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Type</th>
                        <th className="text-left px-2 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Address</th>
                        <th className="text-left px-2 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">City</th>
                        <th className="text-left px-2 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Flow</th>
                        <th className="text-right px-2 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAvailable.slice(0, 200).map(o => (
                        <tr key={o.id} className="border-b border-gray-50 hover:bg-[#D1A054]/5" data-testid={`available-row-${o.order_number}`}>
                          <td className="px-2 py-1.5">
                            <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${o.source === "Scheduling" ? "bg-[#6B5B8D]/12 text-[#6B5B8D]" : "bg-[#2F5D8A]/12 text-[#2F5D8A]"}`}>
                              {o.source || "Dispatch"}
                            </span>
                          </td>
                          <td className="px-2 py-1.5 text-gray-600">{o.due_date || "-"}</td>
                          <td className="px-2 py-1.5 font-mono font-medium text-[#1F2937]">{o.order_number}</td>
                          <td className="px-2 py-1.5 text-gray-600">{o.company}</td>
                          <td className="px-2 py-1.5 text-gray-600">{o.order_type}</td>
                          <td className="px-2 py-1.5 text-gray-700 truncate max-w-[220px]" title={o.address}>{o.address}</td>
                          <td className="px-2 py-1.5 text-gray-600">{o.city}</td>
                          <td className="px-2 py-1.5">
                            <span className="text-gray-500 text-[10px]">{o.flow_type || "-"}</span>
                          </td>
                          <td className="px-2 py-1.5 text-right">
                            <button
                              onClick={() => assignAvailableOrder(o)}
                              disabled={assigning === o.id}
                              className="px-2 py-1 bg-[#A7C4A0] hover:bg-[#8FB388] text-white text-[10px] font-semibold rounded disabled:opacity-50 flex items-center gap-1 ml-auto"
                              data-testid={`available-assign-${o.order_number}`}
                            >
                              {assigning === o.id ? (
                                <div className="animate-spin h-2.5 w-2.5 border border-white border-t-transparent rounded-full" />
                              ) : (
                                <User className="h-2.5 w-2.5" />
                              )}
                              {assigning === o.id ? "..." : "Assign"}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {filteredAvailable.length > 200 && (
                  <p className="px-2 py-1.5 text-[10px] text-gray-400 text-center">
                    Showing first 200 of {filteredAvailable.length}. Refine search to see more.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* View Tabs */}
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1" data-testid="inspector-view-tabs">
          {[
            { id: 'active', label: 'Active', count: activeOrders.length },
            ...(!embedded ? [{ id: 'hold', label: 'On Hold', count: holdOrdersList.length }] : []),
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => { setViewTab(tab.id); setSelectedIds(new Set()); setShowArchived(false); }}
              className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 transition-all ${viewTab === tab.id && !showArchived ? 'bg-white text-[#1F2937] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              data-testid={`inspector-tab-${tab.id}`}
            >
              {tab.label}
              <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${viewTab === tab.id && !showArchived ? 'bg-[#DCE4ED] text-[#2F5D8A]' : 'bg-gray-200 text-gray-500'}`}>{tab.count}</span>
            </button>
          ))}
        </div>
        {!embedded && totalInQa > 0 && (
          <button
            onClick={() => { setShowArchived(!showArchived); if (!showArchived) setViewTab('qa_history'); else setViewTab('active'); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 border transition-colors ${showArchived ? 'bg-gray-700 text-white border-gray-700' : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'}`}
            data-testid="inspector-show-archived-btn"
          >
            <Archive className="h-3 w-3" />
            QA History ({totalInQa})
          </button>
        )}
      </div>

      {/* Message */}
      {message && (
        <div className={`p-3 rounded-lg flex items-center gap-2 text-sm ${
          message.type === "success"
            ? "bg-[#A7C4A0]/15 border border-[#A7C4A0]/40 text-[#6B8F62]"
            : "bg-[#C07070]/12 border border-[#C07070]/30 text-[#8B4545]"
        }`}>
          {message.type === "success" ? <CheckCircle className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
          <span>{message.text}</span>
          <button onClick={() => setMessage(null)} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* Search + All Company Orders (workflow support).
          Office uses this button to look up missing orders and assign them
          to the current inspector without leaving the page context. */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <input
            type="text"
            placeholder="Search orders..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-1.5 bg-white border border-gray-300 rounded text-sm text-[#1F2937] placeholder-gray-400 focus:border-[#D1A054] focus:outline-none"
            data-testid="inspector-orders-search"
          />
        </div>
        {!embedded && (
          <button
            onClick={() => navigate('/dispatch/all-orders')}
            className="px-3 py-1.5 rounded text-xs font-semibold flex items-center gap-1.5 transition-all bg-[#2F5D8A] text-white hover:bg-[#264A6E]"
            title="Look up any order in the company archive and assign it"
            data-testid="inspector-all-company-orders-btn"
          >
            <Database className="h-3.5 w-3.5" />
            All Company Orders
          </button>
        )}
      </div>

      {/* Selection Toolbar — different for each tab */}
      {!embedded && selectedIds.size > 0 && viewTab === 'completed' ? (
        <div className="flex flex-wrap items-center gap-3 px-4 py-2.5 bg-[#D1A054]/10 border border-[#D1A054]/30 rounded-lg" data-testid="inspector-completed-toolbar">
          <span className="text-[#B08A3E] text-sm font-medium">{selectedIds.size} selected</span>
          <button
            onClick={() => setShowReopenModal(true)}
            className="px-3 py-1.5 bg-[#D1A054] hover:bg-[#B08A3E] text-white text-sm font-semibold rounded-lg flex items-center gap-1.5"
            data-testid="inspector-reopen-btn"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Reopen Order
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="px-3 py-1.5 text-gray-500 hover:text-[#1F2937] text-sm"
            data-testid="inspector-completed-clear-selection"
          >
            Clear
          </button>
        </div>
      ) : !embedded && selectedIds.size > 0 && viewTab !== 'completed' ? (
        <div className="flex flex-wrap items-center gap-3 px-4 py-2.5 bg-[#D1A054]/10 border border-[#D1A054]/30 rounded-lg" data-testid="inspector-orders-toolbar">
          <span className="text-[#B08A3E] text-sm font-medium">{selectedIds.size} selected</span>
          <button
            onClick={handleBatchTransmit}
            disabled={processing === "transmit"}
            className="px-3 py-1.5 bg-[#2F5D8A] hover:bg-[#2F5D8A] text-white text-sm font-semibold rounded-lg flex items-center gap-1.5 disabled:opacity-50"
            data-testid="batch-transmit-btn"
          >
            <Send className="h-3.5 w-3.5" />
            {processing === "transmit" ? "Processing..." : "Transmitted by Office"}
          </button>
          {!isRestrictedUser && (
            <button
              onClick={handleBatchClose}
              disabled={processing === "close"}
              className="px-3 py-1.5 bg-[#A7C4A0] hover:bg-[#8FB388] text-white text-sm font-semibold rounded-lg flex items-center gap-1.5 disabled:opacity-50"
              data-testid="batch-close-btn"
            >
              <Lock className="h-3.5 w-3.5" />
              {processing === "close" ? "Processing..." : "Close Selected"}
            </button>
          )}
          <button
            onClick={() => setSelectedIds(new Set())}
            className="px-3 py-1.5 text-gray-500 hover:text-[#1F2937] text-sm"
            data-testid="inspector-orders-clear-selection"
          >
            Clear
          </button>
        </div>
      ) : filteredOrders.length > 0 && (
        <div className="flex items-center gap-2" data-testid="inspector-quick-select-bar">
          <span className="text-gray-400 text-xs uppercase tracking-wide mr-1">Quick Select:</span>
          {[20, 50, 100].map(n => (
            <button
              key={n}
              onClick={() => handleQuickSelect(n)}
              className="px-2.5 py-1 text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-[#1F2937] rounded border border-gray-300 transition-colors"
              data-testid={`inspector-quick-select-${n}`}
            >
              Top {n}
            </button>
          ))}
          <span className="text-gray-300 text-xs ml-1">({filteredOrders.length} visible)</span>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#D1A054]" />
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="text-center py-12 text-gray-400 text-sm" data-testid="inspector-orders-empty">
            {orders.length === 0 && !showArchived ? (
              <div className="space-y-2">
                <p>No active orders assigned</p>
                {!embedded && totalInQa > 0 && <p className="text-[11px]">({totalInQa} orders in QA)</p>}
              </div>
            ) : showArchived && archivedOrders.length === 0 ? (
              'No orders in QA'
            ) : `No matching orders${search ? ' for search' : ''}`}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="inspector-orders-table">
              <thead>
                <tr className="border-b border-gray-200">
                  {!embedded && (
                  <th className="px-3 py-2.5 text-left">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="rounded border-gray-300 bg-gray-50 text-[#D1A054] focus:ring-[#D1A054]"
                      data-testid="inspector-orders-select-all"
                    />
                  </th>
                  )}
                  {columns.map(col => (
                    <th
                      key={col.key}
                      className={`px-3 py-2.5 text-left text-[10px] uppercase font-semibold tracking-wider text-gray-400 whitespace-nowrap ${col.sortable ? "cursor-pointer hover:text-gray-600" : ""}`}
                      onClick={() => col.sortable && handleSort(col.key)}
                    >
                      <div className="flex items-center gap-1">
                        {col.label}
                        {col.sortable && sortBy === col.key && (
                          sortDir === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                        )}
                      </div>
                    </th>
                  ))}
                  {!embedded && <th className="px-3 py-2.5 text-left text-[10px] uppercase font-semibold tracking-wider text-gray-400">Transmitted</th>}
                  {!embedded && <th className="px-3 py-2.5 text-left text-[10px] uppercase font-semibold tracking-wider text-gray-400">Closed</th>}
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map(order => {
                  const isSelected = selectedIds.has(order.id);
                  const statusStyle = STATUS_COLORS[order.status] || "bg-gray-100 text-gray-500 border-gray-300";
                  return (
                    <tr
                      key={order.id}
                      onClick={() => navigate(`/order/${order.id}`)}
                      className={`border-b border-gray-200 transition-colors cursor-pointer ${isSelected ? "bg-[#D1A054]/5" : "hover:bg-gray-50"}`}
                      data-testid={`inspector-order-row-${order.id}`}
                    >
                      {!embedded && (
                      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => handleCheckboxChange(order, e)}
                          className="rounded border-gray-300 bg-gray-50 text-[#D1A054] focus:ring-[#D1A054]"
                          data-testid={`inspector-order-checkbox-${order.id}`}
                        />
                      </td>
                      )}
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{order.due_date}</td>
                      <td className="px-3 py-2 text-[#1F2937] font-medium whitespace-nowrap">{order.order_number}</td>
                      <td className="px-3 py-2 text-gray-600">{order.company}</td>
                      <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{order.order_type}</td>
                      <td className="px-3 py-2 text-gray-600">{order.cliente}</td>
                      <td className="px-3 py-2 text-gray-500 text-xs">{order.address}</td>
                      <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{order.city}</td>
                      <td className="px-3 py-2 text-gray-500">{order.state}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold border ${statusStyle}`}>
                          {order.status}
                        </span>
                      </td>
                      {!embedded && (
                      <td className="px-3 py-2">
                        {order.transmitted === "yes" ? (
                          <span className="text-[#2F5D8A] text-xs font-medium" data-testid={`transmitted-badge-${order.id}`}>Yes</span>
                        ) : (
                          <span className="text-gray-300 text-xs">No</span>
                        )}
                      </td>
                      )}
                      {!embedded && (
                      <td className="px-3 py-2">
                        {order.daily_closed === "yes" ? (
                          <span className="text-[#6B8F62] text-xs font-medium" data-testid={`closed-badge-${order.id}`}>Yes</span>
                        ) : (
                          <span className="text-gray-300 text-xs">No</span>
                        )}
                      </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Transmission Modal — hidden when embedded */}
      {!embedded && showTransmitModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="transmit-modal">
          <div className="bg-white rounded-xl border border-gray-200 p-6 w-full max-w-md shadow-2xl">
            <h3 className="text-[#1F2937] text-lg font-semibold mb-1">Transmit {selectedIds.size} Order{selectedIds.size > 1 ? 's' : ''}</h3>
            <p className="text-gray-500 text-xs mb-4">Select operator and payment responsibility</p>

            {/* Transmitted By */}
            <div className="mb-4">
              <label className="block text-xs text-gray-500 uppercase font-semibold mb-1.5">Transmitted By *</label>
              <div className="flex gap-2">
                {['Vitória', 'Aline'].map(op => (
                  <button
                    key={op}
                    onClick={() => setTransmitOperator(op)}
                    className={`flex-1 px-3 py-2 text-sm font-semibold rounded-lg transition-all border ${
                      transmitOperator === op
                        ? 'bg-[#2F5D8A] text-white border-[#2F5D8A] ring-1 ring-[#2F5D8A]'
                        : 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200'
                    }`}
                    data-testid={`transmit-operator-${op.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')}`}
                  >
                    {op}
                  </button>
                ))}
              </div>
            </div>

            {/* Paid By */}
            <div className="mb-4">
              <label className="block text-xs text-gray-500 uppercase font-semibold mb-1.5">Transmission Paid By *</label>
              <div className="flex gap-2">
                {['Inspector', 'Office'].map(p => (
                  <button
                    key={p}
                    onClick={() => { setTransmitPaidBy(p); if (p === 'Inspector') { setOfficeReason(''); setOfficeReasonDetail(''); } }}
                    className={`flex-1 px-3 py-2 text-sm font-semibold rounded-lg transition-all border ${
                      transmitPaidBy === p
                        ? p === 'Inspector' ? 'bg-[#A7C4A0] text-white border-[#A7C4A0] ring-1 ring-[#A7C4A0]' : 'bg-[#D1A054] text-white border-[#D1A054] ring-1 ring-[#D1A054]'
                        : 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200'
                    }`}
                    data-testid={`transmit-paid-${p.toLowerCase()}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>

            {/* Office Reason (if paid by Office) */}
            {transmitPaidBy === 'Office' && (
              <div className="mb-4 p-3 bg-[#D1A054]/8 border border-[#D1A054]/20 rounded-lg space-y-3">
                <div>
                  <label className="block text-xs text-[#9A7A3E] uppercase font-semibold mb-1.5">Office Payment Reason *</label>
                  <select
                    value={officeReason}
                    onChange={(e) => setOfficeReason(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded text-sm text-[#1F2937] focus:outline-none focus:ring-1 focus:ring-orange-500"
                    data-testid="office-reason-select"
                  >
                    <option value="">Select reason...</option>
                    <option value="Order already closed by office">Order already closed by office</option>
                    <option value="Office requested transmission support">Office requested transmission support</option>
                    <option value="Inspector not responsible">Inspector not responsible</option>
                    <option value="Internal office decision">Internal office decision</option>
                    <option value="Billing adjustment">Billing adjustment</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                {officeReason === 'Other' && (
                  <div>
                    <label className="block text-xs text-gray-500 uppercase mb-1">Details *</label>
                    <textarea
                      value={officeReasonDetail}
                      onChange={(e) => setOfficeReasonDetail(e.target.value)}
                      placeholder="Explain the reason..."
                      className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded text-sm text-[#1F2937] focus:outline-none focus:ring-1 focus:ring-orange-500 min-h-[60px]"
                      data-testid="office-reason-detail"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => { setShowTransmitModal(false); setTransmitOperator(''); setTransmitPaidBy(''); setOfficeReason(''); setOfficeReasonDetail(''); }}
                className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm font-medium rounded-lg"
                data-testid="transmit-cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmTransmit}
                disabled={
                  processing === 'transmit' || !transmitOperator || !transmitPaidBy ||
                  (transmitPaidBy === 'Office' && !officeReason) ||
                  (transmitPaidBy === 'Office' && officeReason === 'Other' && !officeReasonDetail.trim())
                }
                className="flex-1 px-4 py-2 bg-[#2F5D8A] hover:bg-[#3B6D9C] text-white text-sm font-semibold rounded-lg disabled:opacity-40 flex items-center justify-center gap-2"
                data-testid="transmit-confirm"
              >
                <Send className="h-4 w-4" />
                {processing === 'transmit' ? 'Transmitting...' : 'Confirm Transmission'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Close With Actions Modal */}
      {showCloseModal && (
        <CloseWithActionsModal
          orders={filteredOrders.filter(o => selectedIds.has(o.id))}
          inspectorName={inspectorName}
          onClose={() => setShowCloseModal(false)}
          onSuccess={(msg) => {
            const isError = msg.startsWith("Error:");
            setMessage({ type: isError ? "error" : "success", text: msg });
            if (!isError) {
              setSelectedIds(new Set());
              fetchOrders();
            }
            setShowCloseModal(false);
          }}
        />
      )}

      {/* Daily Close Review Modal (Angela 2026-05-09) */}
      {showReviewModal && (
        <DailyCloseReviewModal
          inspectorName={inspectorName}
          routeDate={(() => {
            try {
              return new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York" })
                .format(new Date());
            } catch { return new Date().toISOString().slice(0, 10); }
          })()}
          currentUserRole={"Admin"}
          onClose={() => setShowReviewModal(false)}
        />
      )}

      {/* Reopen Modal */}
      {showReopenModal && (
        <ReopenModal
          orderIds={Array.from(selectedIds)}
          orderNumbers={filteredOrders.filter(o => selectedIds.has(o.id)).map(o => o.order_number)}
          defaultInspector={inspectorName}
          onClose={() => setShowReopenModal(false)}
          onSuccess={(msg) => {
            setMessage({ type: 'success', text: msg });
            setSelectedIds(new Set());
            fetchOrders();
          }}
        />
      )}

      {/* Day Off Modal */}
      {showDayOffModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="day-off-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5 space-y-4">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-full bg-[#D1A054]/15">
                <Coffee className="h-5 w-5 text-[#B08A3E]" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-800">Mark Day Off</h2>
                <p className="text-xs text-gray-500">{inspectorName}</p>
              </div>
              <button onClick={() => setShowDayOffModal(false)} className="ml-auto p-1 text-gray-400 hover:text-gray-700" data-testid="day-off-cancel-x">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1">
                  <Calendar className="h-3 w-3" /> Date *
                </label>
                <input type="date" value={dayOffDate} onChange={e => setDayOffDate(e.target.value)}
                  className="mt-1 w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:border-[#D1A054] focus:outline-none"
                  data-testid="day-off-date-input" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Time</label>
                <input type="time" value={dayOffTime} onChange={e => setDayOffTime(e.target.value)}
                  className="mt-1 w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:border-[#D1A054] focus:outline-none"
                  data-testid="day-off-time-input" />
              </div>
            </div>

            <div>
              <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Comment / Reason</label>
              <textarea value={dayOffComment} onChange={e => setDayOffComment(e.target.value)} rows={3}
                placeholder="E.g. Sick, Personal, Doctor appointment..."
                className="mt-1 w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:border-[#D1A054] focus:outline-none resize-none"
                data-testid="day-off-comment-input" />
            </div>

            <div>
              <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1">
                <Paperclip className="h-3 w-3" /> Proof Attachment * (WhatsApp screenshot, doc...)
              </label>
              <div onClick={() => dayOffFileRef.current?.click()}
                className={`mt-1 border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition-all ${dayOffFile ? "border-[#6B8F62] bg-[#6B8F62]/5" : "border-gray-300 hover:border-[#D1A054]"}`}
                data-testid="day-off-dropzone">
                <input ref={dayOffFileRef} type="file" className="hidden"
                  accept="image/*,.pdf,.doc,.docx,.png,.jpg,.jpeg,.webp,.heic"
                  onChange={e => setDayOffFile(e.target.files[0])}
                  data-testid="day-off-file-input" />
                {dayOffFile ? (
                  <span className="text-xs font-medium text-[#6B8F62] flex items-center justify-center gap-1.5">
                    <FileText className="h-4 w-4" /> {dayOffFile.name}
                  </span>
                ) : (
                  <span className="text-xs text-gray-400 flex items-center justify-center gap-1.5">
                    <Upload className="h-4 w-4" /> Click to upload proof (required)
                  </span>
                )}
              </div>
            </div>

            {dayOffError && (
              <div className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-600 flex items-center gap-1.5" data-testid="day-off-error">
                <AlertCircle className="h-3.5 w-3.5" /> {dayOffError}
              </div>
            )}

            <div className="flex gap-2 pt-1">
              <button onClick={() => { setShowDayOffModal(false); setDayOffError(""); }}
                className="flex-1 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm font-medium rounded-lg"
                data-testid="day-off-cancel-btn">
                Cancel
              </button>
              <button onClick={submitDayOff} disabled={dayOffLoading || !dayOffDate || !dayOffFile}
                className="flex-1 px-3 py-2 bg-[#B08A3E] hover:bg-[#8B6D2E] text-white text-sm font-semibold rounded-lg disabled:opacity-40 flex items-center justify-center gap-1.5"
                data-testid="day-off-submit-btn">
                <Coffee className="h-3.5 w-3.5" />
                {dayOffLoading ? "Saving..." : "Record Day Off"}
              </button>
            </div>

            {dayOffHistory.length > 0 && (
              <div className="pt-3 border-t border-gray-100">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Recent Day Offs</p>
                <div className="space-y-1 max-h-[140px] overflow-y-auto">
                  {dayOffHistory.slice(0, 10).map(r => (
                    <div key={r.id} className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 rounded text-[11px]" data-testid={`day-off-history-${r.id}`}>
                      <Calendar className="h-3 w-3 text-[#B08A3E] flex-shrink-0" />
                      <span className="font-medium text-gray-700">{r.day_off_date}</span>
                      {r.day_off_time && <span className="text-gray-400">{r.day_off_time}</span>}
                      <span className="text-gray-500 truncate flex-1">{r.comment || "—"}</span>
                      {r.proof_url && (
                        <a href={`${process.env.REACT_APP_BACKEND_URL}${r.proof_url}`} target="_blank" rel="noopener noreferrer"
                          className="text-[#2F5D8A] hover:underline text-[10px] font-medium">proof</a>
                      )}
                      <button onClick={() => deleteDayOff(r.id)} className="text-gray-300 hover:text-[#DC2626]" title="Delete">
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default InspectorOrdersPage;
